import { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useModuleAccess, ModuleName, PermissionLevel } from "@/hooks/useModuleAccess";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface ModuleGuardProps {
  module: ModuleName;
  requiredPermission?: PermissionLevel;
  children: ReactNode;
}

export function ModuleGuard({
  module,
  requiredPermission = "view",
  children,
}: ModuleGuardProps) {
  const { permission, isLoading, isError } = useModuleAccess(module);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Recover from a failed permission check.
   *
   * WHY THIS IS NOT JUST `refetch()`: the app-wide QueryClient runs
   * staleTime 2min / gcTime 10min with refetchOnWindowFocus DISABLED
   * (see src/App.tsx). So a bad permission result is CACHED and will not
   * re-fetch on its own for minutes — which is what turns a transient blip
   * into a lockout that outlives its cause.
   *
   * The three caches:
   *   ["user-roles", userId]                 <- the bedrock read that failed
   *   ["module-permissions", userId, roles]  <- DERIVED, keyed on roles
   *   ["all-module-permissions", userId, roles]
   *
   * All are matched by PREFIX (React Query's default). That is load-bearing: the
   * two derived keys embed `userId` and the `roles` ARRAY, so an exact-key match
   * would require the very value we are trying to re-read.
   *
   * MEASURED, because the obvious claim turned out to be false. An earlier
   * version of this comment asserted that resetQueries is REQUIRED because
   * invalidateQueries "leaves the error in place". Mutation-testing refuted it
   * twice: swapping in invalidateQueries passes every test here, including one
   * written specifically to catch a difference in the in-flight UI. There is no
   * behavioural difference in this component, because the spinner shown during
   * recovery comes from the DERIVED query re-enabling, not from the roles query
   * returning to pending. resetQueries is kept for intent — "clear this failed
   * state and start over" is what a Retry button means — NOT for a proven edge.
   * If you swap it, nothing breaks; do not add a claim back without a red test.
   *
   * The two removeQueries calls are DEFENSIVE and are NOT pinned by any test
   * (verified: deleting them leaves the suite green). In the reachable path the
   * derived keys change anyway once `roles` changes, so they are usually a
   * no-op. They cover the narrow case where the retry resolves to the SAME roles
   * value and a stale derivation would otherwise be served for staleTime.
   * Stated rather than implied, so the next reader does not mistake untested
   * code for verified code.
   */
  const handleRetry = () => {
    queryClient.removeQueries({ queryKey: ["module-permissions"] });
    queryClient.removeQueries({ queryKey: ["all-module-permissions"] });
    void queryClient.resetQueries({ queryKey: ["user-roles"] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse text-muted-foreground">
          Checking permissions...
        </div>
      </div>
    );
  }

  // A FAILED permission read is "unknown", never "denied". Rendering Access
  // Denied here would tell a full admin they lack a permission they hold —
  // and give them no way to recover. This branch MUST sit above the
  // userLevel < requiredLevel check below.
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Couldn't verify permissions</CardTitle>
            <CardDescription>
              We couldn't confirm your access level just now. This is a
              connection problem, not a change to your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-x-2">
            <Button onClick={handleRetry}>Retry</Button>
            <Button onClick={() => navigate("/")} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const levels: PermissionLevel[] = ["none", "view", "edit", "admin"];
  const userLevel = levels.indexOf(permission);
  const requiredLevel = levels.indexOf(requiredPermission);

  if (userLevel < requiredLevel) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this module.
              {requiredPermission !== "view" && (
                <span className="block mt-1">
                  Required permission level: <strong>{requiredPermission}</strong>
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/")} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
